import React, { useEffect, useState } from 'react';
import { connectionsAPI } from '../../services/api';

const describeError = (err, fallback) => err?.response?.data?.error || err?.message || fallback;
const intervalLabel = (minutes) => {
  if (minutes === 1440) return 'Daily';
  if (minutes >= 60) return `Every ${minutes / 60} hour${minutes === 60 ? '' : 's'}`;
  return `Every ${minutes} minutes`;
};

/** Which Simpro company to read, on a build with several, and how much history. */
export default function SimproSettings({ connector, connection, companyId, onSaved }) {
  const [options, setOptions] = useState(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    connectionsAPI.options(connector.id, companyId)
      .then((res) => {
        if (cancelled) return;
        const s = connection.definitions || {};
        setOptions(res.data);
        setDraft({
          interval: connection.syncIntervalMinutes,
          companyId: s.companyId ?? '',
          historyMonths: s.historyMonths,
        });
      })
      .catch((err) => { if (!cancelled) setError(describeError(err, 'Could not read the options from Simpro')); });
    return () => { cancelled = true; };
    // Options are read once when the settings open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connector.id, companyId]);

  if (error && !options) return <div className="error-message" role="alert">{error}</div>;
  if (!options || !draft) return <p className="source-hint">Reading the options from Simpro…</p>;

  const set = (patch) => {
    setSaved(false);
    setDraft((d) => ({ ...d, ...patch }));
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      const res = await connectionsAPI.saveSettings(connector.id, companyId, {
        syncIntervalMinutes: draft.interval,
        settings: {
          companyId: draft.companyId === '' ? null : Number(draft.companyId),
          historyMonths: Number(draft.historyMonths),
        },
      });
      setSaved(true);
      onSaved(res.data);
    } catch (err) {
      setError(describeError(err, 'Could not save the settings'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="connection-settings" onSubmit={save}>
      <label className="connection-field">
        <span>Sync frequency</span>
        <select value={draft.interval} onChange={(e) => set({ interval: Number(e.target.value) })}>
          {connector.syncIntervals.map((m) => <option key={m} value={m}>{intervalLabel(m)}</option>)}
        </select>
      </label>

      {options.companies.length > 1 && (
        <label className="connection-field">
          <span>Simpro company</span>
          <select value={draft.companyId} onChange={(e) => set({ companyId: e.target.value })}>
            <option value="">The first company: {options.companies[0].name}</option>
            {options.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
      )}

      <label className="connection-field">
        <span>Months of history</span>
        <input type="number" min="1" max="120" step="1" value={draft.historyMonths} onChange={(e) => set({ historyMonths: e.target.value })} />
      </label>
      <p className="source-hint">Jobs and invoices that are still open or unpaid are read whatever their age.</p>

      {error && <div className="error-message" role="alert">{error}</div>}
      <div className="connection-actions">
        <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>
        {saved && <span className="source-hint connection-saved">Saved. A change to what is read starts a full sync.</span>}
      </div>
    </form>
  );
}
